import React from 'react';
//import PropTypes from 'prop-types';
import { isEqual } from 'lodash';

import {
  Button,
  Collapse,
  Radio,
  RadioGroup,
  FormControlLabel,
  Typography,
} from '@material-ui/core';

import QRCode from 'qrcode.react';

import {
  API_METHOD,
  SECURITY_CHECK_TYPE,
  SECURITY_CHECK_MODE
} from 'basic/config';

import * as api from '_core/utils/api';
import { useVisitorInfo } from '_core/utils/visitorInfo';
import { withLang } from '_core/hocs/withLang';
import { editMobile } from 'basic/forms/editMobile';
import { editFinPassword } from 'basic/forms/editFinPassword';
import { FormWithValidation } from '_core/components/FormWithValidation';
import { SecurityCheck } from '_core/components/SecurityCheck';
import { ModalOuter } from '_core/components/ModalOuter';
import { ModalInner } from '_core/components/ModalInner';
import { ClipboardInput } from '_core/components/ClipboardInput';


const FORM_PROPS = {
  [SECURITY_CHECK_MODE.SMS]: {
    decor: editMobile,
    method: API_METHOD.VISITOR_EDIT_SMSCHECK,
  },
  [SECURITY_CHECK_MODE.FINPASSWD]: {
    decor: editFinPassword,
    method: API_METHOD.VISITOR_EDIT_FINPASSWORD,
  },
};

const INITIAL_FORM_STATE_PROPS = {
  isVisible: false,
  canSubmit: null
};

const INITIAL_FORM_STATE = {
  [SECURITY_CHECK_TYPE.AUTH]: {
    [SECURITY_CHECK_MODE.SMS]: INITIAL_FORM_STATE_PROPS
  },
  [SECURITY_CHECK_TYPE.FINOP]: {
    [SECURITY_CHECK_MODE.SMS]: INITIAL_FORM_STATE_PROPS,
    [SECURITY_CHECK_MODE.FINPASSWD]: INITIAL_FORM_STATE_PROPS
  }
};

export default withLang((props) => {
  const {
    getLangString: l,
    
    type,
    protections,
    securityInfo,
    setSecurityInfo
  } = props;
  
  const visitorInfo = useVisitorInfo();
  
  const [currentMode, setCurrentMode] = React.useState();
  const [gauth, setGauth] = React.useState(null);
  const [forms, setForms] = React.useState(INITIAL_FORM_STATE[type]);
  
  React.useEffect(() => {
    if (!securityInfo)
      return;
    
    if (!currentMode) {
      setCurrentMode(securityInfo[type]?.mode);
      
      return;
    }
    
    setForms(
      Object.keys(forms).reduce(
        (result, mode) => {
          result[mode] = {
            ...forms[mode],
            
            isVisible: currentMode === mode
          }
          return result;
        },
        {}
      )
    );
  }, [securityInfo, currentMode]);
  
  
  if (!currentMode)
    return null;
  
  
  const renderGauthCodeModal = () => {
    return (
      <ModalOuter open={!!gauth} onClose={() => setGauth(null)} maxWidth="sm">
        <ModalInner
          title={l('r._.profile.security.label.gauth')}
          content={
            <>
              <ClipboardInput
                value={gauth?.code}
                label={l('r._.profile.security.label.gauthkey')}
              />
              {gauth ? <QRCode value={gauth.qrcode.content} level="L" size={175} /> : null}
            </>
          }
        />
      </ModalOuter>
    )
  };
  
  const loadAuthKey = (link) => {
    const anchor = document.createElement('a');
    anchor.setAttribute('href', link);
    anchor.setAttribute('type', 'text/plain');
    anchor.setAttribute('download', 'authkey.pem');
    anchor.style.display = 'none';

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };
  
  const canSubmit = () => {
    const mode = currentMode;
    
    if (!securityInfo || !mode)
      return false;
    
    const info = securityInfo[type];
    
    const canSubmitForm = !forms[mode] ? true : forms[mode].canSubmit != null ? forms[mode].canSubmit : false;
    
    return (currentMode !== info?.mode || !info?.isset) && canSubmitForm;
  };
  
  const onSubmit = async () => {
    const mode = currentMode;
    const values = forms[mode] ? forms[mode].values : {};

    try {
      const result = await api.request(API_METHOD.CLIENT_SECURITY_CHANGE, {
        type,
        mode,
        
        ...values
      });

      if (mode === SECURITY_CHECK_MODE.AUTHKEY && result.link) {
        loadAuthKey(result.link);
      } else if (mode === SECURITY_CHECK_MODE.GAUTH && result.code) {
        setGauth(result);
      }

      setSecurityInfo(await api.request(API_METHOD.CLIENT_SECURITY_INFO, {}));
    } catch (error) {
      console.log(error);
    }
  };
  
  const onSubmitCheck = async (check) => {
    return (await check()) && onSubmit();
  };
  
  const renderSubmitButton = () => {
    return (
      <SecurityCheck
        type={type}
        isFromProfile
        login={visitorInfo.info?.login}
        render={({ check, renderCheckForm }) => (
          <>
            <Button
              type="submit"
              disabled={!canSubmit()}
              onClick={() => onSubmitCheck(check)}
            >
              {l('r._.profile.security.action.save')}
            </Button>

            {renderCheckForm()}
          </>
        )}
      />
    )
  };

  const renderOptions = () => {
    return (
      <>
        <Typography variant="body1">{l('$r._.profile.security.label.' + type)}</Typography>
        <RadioGroup name={type} value={currentMode} onChange={(event) => setCurrentMode(event.target.value)}>
          {protections?.map((protection) => protection.type === type && (
            <React.Fragment key={protection.code}>
              <FormControlLabel
                value={protection.code}
                disabled={!securityInfo}
                checked={currentMode === protection.code}
                control={<Radio color="primary" />}
                label={l(protection.title)}
              />
              {renderOptionForm(protection.code)}
            </React.Fragment>
          ))}
        </RadioGroup>
      </>
    )
  };

  const renderOptionForm = (mode) => {
    if (!forms[mode])
      return null;

    return (
      <Collapse in={forms[mode].isVisible}>
        <FormWithValidation
          submitText={l('r._.profile.security.action.save')}
          decor={FORM_PROPS[mode].decor}
          method={FORM_PROPS[mode].method}
          checkRequiredTouched={true}
          renderFormBody={({ outerProps, innerProps }) => {
            const data = {
              canSubmit: forms[mode].canSubmit,
              values: forms[mode].values
            };

            const newData = {
              canSubmit: outerProps.canSubmit(innerProps),
              values: innerProps.values
            };
            
            if (!isEqual(data, newData))
              setForms({ ...forms,  [mode]: { ...forms[mode], ...newData } });
            
            return outerProps.renderFields(innerProps);
          }}
        />
      </Collapse>
    )
  };

  return (
    <>
      {renderOptions()}
      {renderSubmitButton()}
      {renderGauthCodeModal()}
    </>
  );
});
